import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/*
 * Ferestrele de mentenanta ale aplicatiilor de business -
 * `v4/api/src/Controller/BusinessAppsMaintenanceController.php`.
 *
 *   GET  /business-apps/{id}/maintenance               cardul de pe fisa
 *   POST /business-apps/{id}/maintenance/save          adauga / modifica (ID)
 *   POST /business-apps/maintenance/{windowId}/remove  retrage fereastra
 *   POST /business-apps/maintenance/runs/{runId}/end   "End now"
 *   GET  /business-apps/maintenance/active             aplicatiile in mentenanta acum (insigna)
 *
 * In fereastra, alarmele componentelor nu deschid tichete si nu trimit nimic:
 * cronul pune pe fiecare aparat un rand de liniste ("Quiet hours", mod hush).
 */

export type MaintenanceOccurrence = {
  startTs: number
  startsAt: string
  endsAt: string
  timezone: string
  inProgress: boolean
}

export type MaintenanceWindow = {
  id: number
  name: string
  description: string
  /** 1 o data, 2 saptamanal */
  kind: 1 | 2
  schedule: string
  startsAt: string
  endsAt: string
  /** 1 = luni .. 7 = duminica */
  days: number[]
  startTime: string
  endTime: string
  timezone: string
  announce: boolean
  announceHours: number
  announceTo: number[]
  next: MaintenanceOccurrence | null
}

export type MaintenanceRun = {
  id: number
  windowId: number
  name: string
  startsAt: string
  endsAt: string
  timezone: string
  state: 'scheduled' | 'active' | 'ended' | 'cancelled'
  active: boolean
  endedEarly: boolean
  announced: boolean
  announcedCount: number
  devices: number
  alarmsHushed: number
  ticketsNoted: number
}

export type MaintenancePerson = { id: number; name: string; email: string }

export type AppMaintenanceCard = {
  ready: boolean
  message?: string
  canEdit?: boolean
  timezone?: string
  windows?: MaintenanceWindow[]
  next?: (MaintenanceOccurrence & { windowId: number; name: string }) | null
  active?: MaintenanceRun | null
  history?: MaintenanceRun[]
  people?: MaintenancePerson[]
}

export type ActiveMaintenance = {
  ready: boolean
  apps: { appId: number; window: string; until: string; timezone: string }[]
}

export type MaintenanceWritten = { status: 'OK' | 'ERROR'; message: string; id?: number }

export function appMaintenanceQuery(appId: number) {
  return queryOptions({
    queryKey: ['business-apps', 'maintenance', appId],
    queryFn: () => apiGet<AppMaintenanceCard>(`/business-apps/${appId}/maintenance`),
    enabled: appId > 0,
  })
}

export function activeMaintenanceQuery() {
  return queryOptions({
    queryKey: ['business-apps', 'maintenance', 'active'],
    queryFn: () => apiGet<ActiveMaintenance>('/business-apps/maintenance/active'),
    staleTime: 60_000,
  })
}

export function saveMaintenanceWindow(appId: number, fields: Record<string, PostValue>) {
  return apiPost<MaintenanceWritten>(`/business-apps/${appId}/maintenance/save`, fields)
}

export function removeMaintenanceWindow(windowId: number) {
  return apiPost<MaintenanceWritten>(`/business-apps/maintenance/${windowId}/remove`, {})
}

export function endMaintenanceNow(runId: number) {
  return apiPost<MaintenanceWritten>(`/business-apps/maintenance/runs/${runId}/end`, {})
}
